import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
} from '@mui/material';
import { Person, Email, Phone, LocationOn, Cake } from '@mui/icons-material';
import { format } from 'date-fns';
import WhatsAppLink from '../common/WhatsAppLink';

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  dateOfBirth?: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
  };
  preferences: {
    skinType: string;
    hairType: string;
    allergies: string[];
    notes: string;
  };
  loyaltyPoints: number;
  totalSpent: number;
  lastVisit?: string;
  visitCount: number;
  status: string;
}

interface ServiceHistory {
  id: string;
  serviceName: string;
  date: string;
  amount: number;
  staffName: string;
  status: string;
}

interface CustomerDetailProps {
  open: boolean;
  onClose: () => void;
  customer: Customer | null;
  serviceHistory?: ServiceHistory[];
  onEdit?: () => void;
}

const CustomerDetail: React.FC<CustomerDetailProps> = ({
  open,
  onClose,
  customer,
  serviceHistory = [],
  onEdit,
}) => {
  if (!customer) return null;

  const formatAddress = (address: Customer['address']) => {
    const parts = [address.street, address.city, address.state, address.pincode];
    return parts.filter(Boolean).join(', ');
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'blocked':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            <Person />
          </Avatar>
          <Box>
            <Typography variant="h5">
              {customer.firstName} {customer.lastName}
            </Typography>
            <Chip
              label={customer.status}
              color={getStatusColor(customer.status) as any}
              size="small"
            />
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Customer Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                <List>
                  {customer.email && (
                    <ListItem>
                      <Email sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText primary={customer.email} secondary="Email" />
                    </ListItem>
                  )}
                  <ListItem
                    secondaryAction={
                      <WhatsAppLink
                        phone={customer.phone}
                        message={`Hi ${customer.firstName}, this is from your salon.`}
                      />
                    }
                  >
                    <Phone sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText primary={customer.phone} secondary="Phone" />
                  </ListItem>
                  {customer.dateOfBirth && (
                    <ListItem>
                      <Cake sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText 
                        primary={format(new Date(customer.dateOfBirth), 'MMM dd, yyyy')} 
                        secondary="Date of Birth" 
                      />
                    </ListItem>
                  )}
                  {formatAddress(customer.address) && (
                    <ListItem>
                      <LocationOn sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText 
                        primary={formatAddress(customer.address)} 
                        secondary="Address" 
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Customer Stats */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Stats
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {customer.visitCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Visits
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        ₹{customer.totalSpent.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Spent
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">
                        {customer.loyaltyPoints}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Loyalty Points
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1">
                        {customer.lastVisit 
                          ? format(new Date(customer.lastVisit), 'MMM dd, yyyy')
                          : 'Never'
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Last Visit
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Preferences */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Preferences
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Skin Type
                  </Typography>
                  <Typography variant="body1">
                    {customer.preferences.skinType || 'Not specified'}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Hair Type
                  </Typography>
                  <Typography variant="body1">
                    {customer.preferences.hairType || 'Not specified'}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Allergies
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {customer.preferences.allergies.length > 0 ? (
                      customer.preferences.allergies.map((allergy) => (
                        <Chip key={allergy} label={allergy} size="small" color="error" variant="outlined" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No allergies recorded
                      </Typography>
                    )}
                  </Box>
                </Box>
                {customer.preferences.notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {customer.preferences.notes}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Service History */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Service History
                </Typography>
                {serviceHistory.length > 0 ? (
                  <List>
                    {serviceHistory.slice(0, 5).map((service, index) => (
                      <React.Fragment key={service.id}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText
                            primary={service.serviceName}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {format(new Date(service.date), 'MMM dd, yyyy')} • {service.staffName}
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                  ₹{service.amount}
                                </Typography>
                              </Box>
                            }
                          />
                          <Chip
                            label={service.status}
                            size="small"
                            color={service.status === 'completed' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </ListItem>
                        {index < serviceHistory.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary">
                    No service history available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onEdit && (
          <Button onClick={onEdit} variant="contained">
            Edit Customer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDetail;